import { useState } from "react";
import FixedPage from "src/components/Layout/FixedPage";
import { lang } from "src/language/Strings";

interface PopupImageGalleryProps {
  images: { image: string; thumbnail: string; depiction?: string; comment?: string; action?: string }[] | undefined;
  id?: string;
  maxHeight?: string;
  editMode?: boolean;
}



export function PopupImageGallery({
  images,
  id = "popup-image-gallery",
  maxHeight = "h-40",
  editMode = false,
}: PopupImageGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullSize, setIsFullSize] = useState(false);
  const [fullSizeImageIndex, setFullSizeImageIndex] = useState(0);

  if (!images || images.length === 0) return null;

  const filteredImages = images.filter((i) => i.image || i.thumbnail);
  if (filteredImages.length === 0) return null;

  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? filteredImages.length - 1 : prev - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === filteredImages.length - 1 ? 0 : prev + 1));
  };

  const openFullSize = (index: number) => {
    setFullSizeImageIndex(index);
    setIsFullSize(true);
  };

  const navigateFullSize = (direction: "prev" | "next") => {
    if (direction === "prev") {
      setFullSizeImageIndex(
        (prev) => (prev === 0 ? filteredImages.length - 1 : prev - 1)
      );
    } else {
      setFullSizeImageIndex(
        (prev) => (prev === filteredImages.length - 1 ? 0 : prev + 1)
      );
    }
  };

  const currentImage = filteredImages[currentIndex];
  const imageUrl = currentImage.image || currentImage.thumbnail;

  return (
    <>
      <div
        id={`${id}-container`}
        className={`relative ${maxHeight} h-40 w-full max-w-full bg-Grey2 rounded-lg overflow-hidden border border-Grey4 group`}
      >
        {/* Main Image */}
        <div className="relative w-full h-full max-w-full flex items-center justify-center overflow-hidden">
          <img
            src={imageUrl}
            alt={`Image ${currentIndex + 1}`}
            className={`${maxHeight} max-w-full w-auto h-full object-contain cursor-pointer transition-opacity duration-200`}
            onClick={() => openFullSize(currentIndex)}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              if (currentImage.thumbnail && target.src !== currentImage.thumbnail) {
                target.src = currentImage.thumbnail;
              }
            }}
          />

          {/* Navigation Arrows - Show on hover */}
          {filteredImages.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-Grey9/80 hover:bg-Grey9 text-White p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                aria-label="Previous image"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                onClick={handleNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-Grey9/80 hover:bg-Grey9 text-White p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                aria-label="Next image"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </>
          )}

          {/* Image Counter */}
          {filteredImages.length > 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-Grey9/80 text-White px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
              {currentIndex + 1} / {filteredImages.length}
            </div>
          )}

          {/* Thumbnail Strip (if multiple images) */}
          {filteredImages.length > 1 && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-Grey9/90 to-transparent p-2 max-w-full overflow-hidden">
              <div className="flex gap-1.5 overflow-x-auto justify-center max-w-full [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {filteredImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentIndex(index);
                    }}
                    className={`flex-shrink-0 w-12 h-12 rounded border-2 overflow-hidden transition-all duration-200 ${
                      index === currentIndex
                        ? "border-PrimaryGreen shadow-md scale-110"
                        : "border-Grey4 opacity-70 hover:opacity-100 hover:border-Grey7"
                    }`}
                  >
                    <img
                      src={(img.thumbnail && img.thumbnail.trim() !== "") ? img.thumbnail : img.image}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        // If thumbnail fails to load, fallback to the main image
                        if (img.image && target.src !== img.image) {
                          target.src = img.image;
                        }
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Image Information Display */}
      {currentImage && (currentImage.depiction || currentImage.comment || currentImage.action) && (
        <div className="w-full bg-Grey1 rounded-lg border border-Grey4 p-3 space-y-2">
          {currentImage.depiction && (
            <div className="flex flex-row gap-2">
              <div className="text-sm font-semibold text-Grey7">{lang.objectType}:</div>
              <div className="text-sm text-Grey9">{currentImage.depiction}</div>
            </div>
          )}
          {currentImage.comment && (
            <div className="flex flex-row gap-2">
              <div className="text-sm font-semibold text-Grey7">{lang.description}:</div>
              <div className="text-sm text-Grey9">{currentImage.comment}</div>
            </div>
          )}
          {currentImage.action && (
            <div className="flex flex-row gap-2">
              <div className="text-sm font-semibold text-Grey7">{lang.action}:</div>
              <div className="text-sm text-Grey9">{currentImage.action}</div>
            </div>
          )}
        </div>
      )}

      {/* Full Size Modal */}
      {isFullSize && (
        <FixedPage onOutsideClick={() => setIsFullSize(false)} fullScreen={true}>
          <div className="relative w-full h-full flex items-center justify-center  p-4">
            

            {filteredImages.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateFullSize("prev")}}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-Grey9/80 hover:bg-Grey9 text-White p-3 rounded-full shadow-lg z-20 transition-all"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateFullSize("next")}}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-Grey9/80 hover:bg-Grey9 text-White p-3 rounded-full shadow-lg z-20 transition-all"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-Grey9/80 text-White px-4 py-2 rounded-full text-sm font-semibold shadow-lg z-20">
                  {fullSizeImageIndex + 1} / {filteredImages.length}
                </div>
              </>
            )}

            <img
              src={filteredImages[fullSizeImageIndex].image || filteredImages[fullSizeImageIndex].thumbnail}
              alt={`Full size image ${fullSizeImageIndex + 1}`}
              className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
              onClick={() => setIsFullSize(false)}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                const img = filteredImages[fullSizeImageIndex];
                if (img.thumbnail && target.src !== img.thumbnail) {
                  target.src = img.thumbnail;
                }
              }}
            />
          </div>
        </FixedPage>
      )}
    </>
  );
}

